/**
 * Admin Page - Refactored (New Version)
 * 
 * Menu Structure:
 * - Dashboard (Analytics)
 * - Users (Management + Verification)
 * - Content (Categories + Translations)
 * - Islands (Dedicated tab)
 * - Advertising (Banners + Placements + Analytics)
 * - Events
 * - Moderation (Reports + Inquiries)
 * - System (Settings + Logs + Health)
 */
import React, { useState, useEffect, Suspense, lazy, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import axios from '../lib/axios';
import { Loader2, Shield, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useAuth, API } from '../App';

// Admin Components
import { AdminProvider, AdminSidebar } from '../components/admin';

// Lazy load all tab components
const AdminAnalytics = lazy(() => import('../components/admin/AdminAnalytics'));
const AdminBoostAnalytics = lazy(() => import('../components/admin/AdminBoostAnalytics'));
const AdminUserManagement = lazy(() => import('../components/admin/AdminUserManagement'));
const AdminIslandSettings = lazy(() => import('../components/admin/AdminIslandSettings'));
const AdminSystemHealth = lazy(() => import('../components/admin/AdminSystemHealth'));
const AdminReports = lazy(() => import('../components/admin/AdminReports'));
const AdminInquiries = lazy(() => import('../components/admin/AdminInquiries'));
const AdminLogs = lazy(() => import('../components/admin/AdminLogs'));
const AdminSettings = lazy(() => import('../components/admin/AdminSettings'));
const AdminEvents = lazy(() => import('../components/admin/AdminEvents'));
const AdminCategories = lazy(() => import('../components/admin/AdminCategories'));
const AdminAdvertising = lazy(() => import('../components/admin/AdminAdvertising'));
const AdminAnnouncements = lazy(() => import('../components/admin/AdminAnnouncements'));
const AdminBusinessDocuments = lazy(() => import('../components/admin/AdminBusinessDocuments'));
const AdminReferrals = lazy(() => import('../components/admin/AdminReferrals'));
const AdminListingMaintenance = lazy(() => import('../components/admin/AdminListingMaintenance'));
const AdminListings = lazy(() => import('../components/admin/AdminListings'));
const AdminTicker = lazy(() => import('../components/admin/AdminTicker'));
const AdminHotspots = lazy(() => import('../components/admin/AdminHotspots'));

// Legacy components (Translations still needs token prop)
const TranslationManager = lazy(() => import('../components/TranslationManager'));

// Loader component
const TabLoader = () => (
  <div className="flex items-center justify-center py-20">
    <Loader2 className="w-8 h-8 animate-spin text-primary" />
  </div>
);

const AdminPageNew = () => {
  const { user, token } = useAuth();
  
  // Navigation state
  const [activeTab, setActiveTab] = useState('dashboard');
  const [subTab, setSubTab] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Admin verification
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminCheckDone, setAdminCheckDone] = useState(false);
  
  // Pending counts for badges
  const [pendingCounts, setPendingCounts] = useState({
    verification: 0,
    reports: 0,
    inquiries: 0,
    events: 0
  });
  
  // Check admin status
  useEffect(() => {
    const checkAdmin = async () => {
      // With httpOnly cookies, we check user instead of token
      if (!user) {
        setAdminCheckDone(true);
        return;
      }
      
      try {
        const response = await axios.get(`${API}/users/me/is-admin`);
        setIsAdmin(response.data.is_admin);
      } catch (error) {
        console.error('Admin check failed:', error);
        setIsAdmin(false);
      } finally {
        setAdminCheckDone(true);
      }
    };
    
    checkAdmin();
  }, [user]);
  
  // Fetch pending counts
  useEffect(() => {
    const fetchCounts = async () => {
      if (!user || !isAdmin) return;
      
      try {
        const [reportsRes, inquiriesRes, businessDocsRes] = await Promise.all([
          axios.get(`${API}/admin/reported-listings?status=pending`).catch(() => ({ data: [] })),
          axios.get(`${API}/admin/inquiries?status=new`).catch(() => ({ data: [] })),
          axios.get(`${API}/admin/business-documents?status=pending_review&limit=1`).catch(() => ({ data: { counts: { pending: 0 } } }))
        ]);
        
        setPendingCounts({
          reports: reportsRes.data?.reports?.length || reportsRes.data?.length || 0,
          inquiries: inquiriesRes.data?.inquiries?.length || inquiriesRes.data?.length || 0,
          businessDocs: businessDocsRes.data?.counts?.pending || 0,
          events: 0
        });
      } catch (error) {
        console.error('Failed to fetch counts:', error);
      }
    };
    
    if (isAdmin) {
      fetchCounts();
    }
  }, [user, isAdmin]);
  
  // Handle reports count update
  const handleReportsCountChange = useCallback((count) => {
    setPendingCounts(prev => ({ ...prev, reports: typeof count === 'function' ? count(prev.reports) : count }));
  }, []);
  
  // Handle inquiries count update
  const handleInquiriesCountChange = useCallback((count) => {
    setPendingCounts(prev => ({ ...prev, inquiries: typeof count === 'function' ? count(prev.inquiries) : count }));
  }, []);
  
  // Loading state
  if (!adminCheckDone) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }
  
  // Not admin - redirect
  if (!isAdmin) {
    toast.error('Admin access required');
    return <Navigate to="/" replace />;
  }
  
  // Render active tab content
  const renderContent = () => {
    return (
      <Suspense fallback={<TabLoader />}>
        {/* Dashboard */}
        {activeTab === 'dashboard' && subTab === 'overview' && <AdminAnalytics />}
        {activeTab === 'dashboard' && subTab === 'boost' && <AdminBoostAnalytics />}
        {activeTab === 'dashboard' && !subTab && <AdminAnalytics />}
        
        {/* Users */}
        {activeTab === 'users' && subTab === 'management' && <AdminUserManagement />}
        {activeTab === 'users' && subTab === 'business-verification' && <AdminBusinessDocuments />}
        {activeTab === 'users' && !subTab && <AdminUserManagement />}
        
        {/* Content */}
        {activeTab === 'content' && subTab === 'categories' && <AdminCategories />}
        {activeTab === 'content' && subTab === 'translations' && <TranslationManager token={token} />}
        {activeTab === 'content' && subTab === 'listings' && <AdminListings />}
        {activeTab === 'content' && subTab === 'listing-maintenance' && <AdminListingMaintenance />}
        {activeTab === 'content' && !subTab && <AdminCategories />}
        
        {/* Islands - Dedicated Tab */}
        {activeTab === 'islands' && <AdminIslandSettings />}
        
        {/* Advertising - Unified component */}
        {activeTab === 'advertising' && <AdminAdvertising />}
        
        {/* News Ticker */}
        {activeTab === 'ticker' && <AdminTicker />}
        
        {/* Featured Partners / Hotspots */}
        {activeTab === 'hotspots' && <AdminHotspots />}
        
        {/* Referrals - Share & Win Campaign */}
        {activeTab === 'referrals' && <AdminReferrals />}
        
        {/* Events */}
        {activeTab === 'events' && <AdminEvents />}
        
        {/* Moderation */}
        {activeTab === 'moderation' && subTab === 'reports' && <AdminReports onCountChange={handleReportsCountChange} />}
        {activeTab === 'moderation' && subTab === 'inquiries' && <AdminInquiries onCountChange={handleInquiriesCountChange} />}
        {activeTab === 'moderation' && !subTab && <AdminReports onCountChange={handleReportsCountChange} />}
        
        {/* System */}
        {activeTab === 'system' && subTab === 'announcements' && <AdminAnnouncements />}
        {activeTab === 'system' && subTab === 'settings' && <AdminSettings />}
        {activeTab === 'system' && subTab === 'logs' && <AdminLogs />}
        {activeTab === 'system' && subTab === 'health' && <AdminSystemHealth />}
        {activeTab === 'system' && !subTab && <AdminAnnouncements />}
      </Suspense>
    );
  };
  
  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <AdminProvider token={token}>
        {/* Mobile Admin Header - Outside flex container */}
        <div className="md:hidden pt-16">
          <AdminSidebar
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            subTab={subTab}
            setSubTab={setSubTab}
            pendingCounts={pendingCounts}
            mobileMenuOpen={mobileMenuOpen}
            setMobileMenuOpen={setMobileMenuOpen}
            isMobileHeader={true}
          />
        </div>
        
        <div className="flex min-h-[calc(100vh-64px)]">
          {/* Desktop Sidebar Only */}
          <div className="hidden md:block">
            <AdminSidebar
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              subTab={subTab}
              setSubTab={setSubTab}
              pendingCounts={pendingCounts}
              mobileMenuOpen={mobileMenuOpen}
              setMobileMenuOpen={setMobileMenuOpen}
              isMobileHeader={false}
            />
          </div>
          
          {/* Main Content */}
          <main className="flex-1 overflow-y-auto">
            {/* Page Header - Desktop only - sticky below navbar */}
            <div className="hidden md:block sticky top-16 z-10 bg-background/95 backdrop-blur-sm border-b border-border">
              <div className="flex items-center justify-between px-6 py-3">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
                    <Shield className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold">Admin Dashboard</h1>
                    <p className="text-sm text-muted-foreground">
                      Welcome back, {user?.name || 'Admin'}
                    </p>
                  </div>
                </div>
                
                {/* Quick Stats Badges */}
                <div className="flex items-center gap-3">
                  {pendingCounts.verification > 0 && (
                    <div className="flex items-center gap-2 px-4 py-2 bg-amber-500/10 text-amber-600 rounded-xl border border-amber-500/20">
                      <span className="w-2 h-2 bg-amber-500 rounded-full animate-pulse" />
                      <span className="font-medium">{pendingCounts.verification} pending verification</span>
                    </div>
                  )}
                  {pendingCounts.reports > 0 && (
                    <div className="flex items-center gap-2 px-4 py-2 bg-red-500/10 text-red-600 rounded-xl border border-red-500/20">
                      <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                      <span className="font-medium">{pendingCounts.reports} reports</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Tab Content */}
            <div className="p-4 md:p-6 md:mt-20 pb-24 md:pb-6">
              {renderContent()}
            </div>
          </main>
        </div>
      </AdminProvider>
      
      <div className="hidden md:block">
        <Footer />
      </div>
    </div>
  );
};

export default AdminPageNew;
